import { useMemo } from 'react';
import { createInfiniteQuery, createMutation, createQuery } from 'react-query-kit';

import { patchData } from 'services/http/http';
import { queryClient } from 'services/http/queryClient';
import { isStatusCodeZeroError } from 'support/helpers/errors/errors';
import { determineInternalType } from 'support/helpers/transactions/transactions';
import { addQueryParametersToUrl, buildFilterUrl } from 'support/helpers/urls/urls';
import {
  DocumentType,
  ErrorDialog,
  PaginationDTO,
  RelationshipState,
  TransactionDTO,
  TransactionInternalType,
  TransactionOriginApp,
} from 'support/types';
import { Filter } from 'support/types/filters';
import portalSchema from 'support/types/schema-portal';

import { createData, fetchData, updateData } from '../../http/http';
import { useConnection, useInfiniteConnectionsV2 } from '../connections/connections';
import { getMessageOrEnvelope } from '../helpers/helpers';
import { usePartner } from '../partners/partners';
import { CONNECTIONS_QUERY_KEY, PARTNERS_QUERY_KEY, RELATIONSHIPS_QUERY_KEY } from '../queryKeys';

export type CreateTradeRequestRequest =
  portalSchema.paths['/v1/partners/trade-requests']['post']['requestBody']['content']['application/json'];

type CreateTradeRequestResponse =
  portalSchema.paths['/v1/partners/trade-requests']['post']['responses']['201']['content']['application/json'];

export type RelationshipsResponseDTO =
  portalSchema.paths['/v1/relationships']['get']['responses']['200']['content']['application/json'];

export type RelationshipResponseDTO =
  portalSchema.paths['/v1/relationships/{relationshipId}']['get']['responses']['200']['content']['application/json'];

type DeactiveRelationshipResponseDTO =
  portalSchema.paths['/v1/relationships/{relationshipId}/deactivate']['post']['responses']['200']['content']['application/json'];

type EditInternalIdentifiersRequest =
  portalSchema.paths['/v1/relationships/{relationshipId}/edit-internal-identifiers']['post']['requestBody']['content']['application/json'];

type EditInternalIdentifiersResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/edit-internal-identifiers']['post']['responses']['200']['content']['application/json'];

type ResendEmailInvitationRequest = Required<
  portalSchema.paths['/v1/partners/{partnerId}/resend-trade-request-invitation-email']['post']
>['requestBody']['content']['application/json'];

type ResendEmailInvitationResponse =
  portalSchema.paths['/v1/partners/{partnerId}/resend-trade-request-invitation-email']['post']['responses']['200']['content']['application/json'];

type RequestUnavailableDocumentTypesRequest =
  portalSchema.paths['/v1/partners/request-unsupported-document-types']['post']['requestBody']['content']['application/json'];

type RequestUnavailableDocumentTypesResponse =
  portalSchema.paths['/v1/partners/request-unsupported-document-types']['post']['responses']['200']['content']['application/json'];

export const useRequestUnavailableDocumentTypes = createMutation<
  RequestUnavailableDocumentTypesResponse,
  RequestUnavailableDocumentTypesRequest,
  Error
>({
  mutationFn: async (payload) => {
    const { data } = await createData<RequestUnavailableDocumentTypesResponse>(
      '/v1/partners/request-unsupported-document-types',
      payload,
    );

    return data;
  },
});

export const useCreateTradeRequest = createMutation<CreateTradeRequestResponse, CreateTradeRequestRequest, Error>({
  mutationFn: async (payload) => {
    const { data } = await createData<CreateTradeRequestResponse>('/v1/partners/trade-requests', payload);
    return data;
  },
  onSuccess: (_data, variables) => {
    const partnerIds = variables.tradeRequests.map((tr) => tr.partnerId);

    return Promise.all([
      ...partnerIds.map((partnerId) => queryClient.invalidateQueries(usePartner.getKey({ id: partnerId }))),
      queryClient.invalidateQueries([CONNECTIONS_QUERY_KEY]),
      queryClient.invalidateQueries(useConnection.getKey()),
      queryClient.invalidateQueries(useInfiniteConnectionsV2.getKey()),
      queryClient.invalidateQueries([RELATIONSHIPS_QUERY_KEY]),
      queryClient.invalidateQueries([PARTNERS_QUERY_KEY]),
    ]);
  },
});

export const useRelationship = createQuery<RelationshipResponseDTO['data'], { relationshipId?: string }, Error>({
  primaryKey: `${RELATIONSHIPS_QUERY_KEY}/:relationshipId`,
  queryFn: async ({ queryKey: [, variables] }) => {
    const { data } = await fetchData<RelationshipResponseDTO>('/v1/relationships/' + variables.relationshipId);

    return data?.data;
  },
  enabled: (_, variables) => !!variables.relationshipId,
});

type RelationshipDocumentCreationData = {
  relationshipId: string;
  data: any;
  waitForMessage?: boolean;
  application?: TransactionOriginApp;
  // When editing and reprocessing an existing document, the id of the original message so the
  // reprocessed document inherits its TEST/LIVE flow classification instead of re-deriving it.
  originalMessageId?: string;
};

export type RelationshipDocumentCreationSuccessResponse = {
  transactionId: string;
  transactionType: TransactionInternalType;
};

export const useRelationshipDocumentCreation = createMutation<
  RelationshipDocumentCreationSuccessResponse,
  RelationshipDocumentCreationData,
  Error
>({
  retry: (failureCount, error) => failureCount < 2 && isStatusCodeZeroError(error),
  retryDelay: 1000,
  mutationFn: async ({ relationshipId, data, waitForMessage = true, application, originalMessageId }) => {
    const url = addQueryParametersToUrl(`/v1/relationships/${relationshipId}/sender_envelopes`, {
      origin: application ?? undefined,
    });

    const response = await createData<any>(url, { data, original_message_id: originalMessageId });

    if (![200, 201].includes(response.status) || !response.data || !('procuros_transaction_id' in response.data.data)) {
      switch (response.status) {
        case 202:
          throw new Error('message' in response.data ? response.data.message : ErrorDialog.internalServer);
        default:
          // Should not happen. Any non-2XX response should throw an error earlier
          throw new Error(ErrorDialog.internalServer);
      }
    }

    const createdSenderEnvelopeId = response.data.data.procuros_transaction_id;
    return await getMessageOrEnvelope(createdSenderEnvelopeId, waitForMessage);
  },
});

export const useInfiniteRelationships = createInfiniteQuery<RelationshipsResponseDTO, { query?: Filter }, Error>({
  primaryKey: RELATIONSHIPS_QUERY_KEY,
  queryFn: async ({ queryKey: [_, variables], pageParam }) => {
    const urlParams = buildFilterUrl(
      { filterValues: [], ...variables.query, cursor: pageParam },
      { forApiRequest: true },
    );
    const { data } = await fetchData<RelationshipsResponseDTO>('/v1/relationships' + urlParams);

    return data;
  },
  getNextPageParam: (lastPage) => {
    return lastPage.meta.next_cursor;
  },
  getPreviousPageParam: (_, allPages) => allPages.at(-1)?.meta.prev_cursor,
});

export const useResendEmailInvitation = createMutation<
  ResendEmailInvitationResponse,
  { partnerId: string } & ResendEmailInvitationRequest,
  Error
>({
  mutationFn: async (variables) => {
    const { data } = await createData<ResendEmailInvitationResponse>(
      `/v1/partners/${variables.partnerId}/resend-trade-request-invitation-email`,
      {
        language: variables.language,
        email: variables.email,
        name: variables.name,
      },
    );
    return data;
  },
});

export const useDeactivateRelationship = createMutation<
  DeactiveRelationshipResponseDTO,
  { relationshipId: string; partnerId: string },
  Error
>({
  mutationFn: async ({ relationshipId }) => {
    const { data } = await createData<DeactiveRelationshipResponseDTO>(
      `/v1/relationships/${relationshipId}/deactivate`,
    );
    return data;
  },
});

export const useEditTradeRequestPartnerIdentifier = createMutation<
  EditInternalIdentifiersResponse,
  EditInternalIdentifiersRequest & { relationshipId: string },
  Error
>({
  mutationFn: async ({ relationshipId, partyIdentifier, partyIdentifierDomain }) => {
    const { data } = await createData<EditInternalIdentifiersResponse, EditInternalIdentifiersRequest>(
      `/v1/relationships/${relationshipId}/edit-internal-identifiers`,
      {
        partyIdentifier,
        partyIdentifierDomain,
      },
    );

    return data;
  },
});

type SendTestmessageRequest = Required<
  portalSchema.paths['/v1/relationships/{id}/testing-flow/send-documents']['patch']
>['requestBody']['content']['application/json'] & { relationshipId: string };

type SendTestmessageResponse =
  portalSchema.paths['/v1/relationships/{id}/testing-flow/send-documents']['patch']['responses']['200']['content']['application/json'];

export const useSendTestDocuments = createMutation<SendTestmessageResponse, SendTestmessageRequest, Error>({
  mutationFn: async ({ relationshipId, messages }) => {
    const { data } = await patchData<SendTestmessageResponse>(
      `/v1/relationships/${relationshipId}/testing-flow/send-documents`,
      { messages },
    );
    return data;
  },
});

type ReceiveTestMessageRequest = Required<
  portalSchema.paths['/v1/relationships/{id}/testing-flow/deliver-documents']['patch']
>['parameters']['path'];

type ReceiveMessageResponse =
  portalSchema.paths['/v1/relationships/{id}/testing-flow/deliver-documents']['patch']['responses']['200']['content']['application/json'];

export const useReceiveTestMessage = createMutation<ReceiveMessageResponse, ReceiveTestMessageRequest, Error>({
  mutationFn: async ({ id: relationshipId }) => {
    const { data } = await patchData<ReceiveMessageResponse>(
      `/v1/relationships/${relationshipId}/testing-flow/deliver-documents`,
    );
    return data;
  },
});

type ReviewTestMessageRequest = Required<
  portalSchema.paths['/v1/relationships/{id}/testing-flow/review']['patch']
>['requestBody']['content']['application/json'];

type ReviewTestMessageResponse =
  portalSchema.paths['/v1/relationships/{id}/testing-flow/review']['patch']['responses']['200']['content']['application/json'];

export const useReviewTestMessage = createMutation<
  ReviewTestMessageResponse,
  ReviewTestMessageRequest & { relationshipId: string },
  Error
>({
  mutationFn: async ({ relationshipId, state, go_live_date, activate_connection_immediately, reason }) => {
    const { data } = await patchData<ReviewTestMessageResponse>(
      `/v1/relationships/${relationshipId}/testing-flow/review`,
      { state, go_live_date, activate_connection_immediately, reason },
    );
    return data;
  },
});

type RelationshipTestDocumentsResponse = Required<
  portalSchema.paths['/v1/relationships/{id}/testing-flow/documents']['get']
>['responses']['200']['content']['application/json'];

export const useRelationshipTestDocuments = createQuery<
  PaginationDTO<TransactionDTO>,
  { query?: Filter; relationshipId: string },
  Error
>({
  primaryKey: `${RELATIONSHIPS_QUERY_KEY}/testing-documents`,
  queryFn: async ({ queryKey: [_, variables] }) => {
    const urlParams = buildFilterUrl({ filterValues: [], ...variables.query }, { forApiRequest: true });
    const { data } = await fetchData<RelationshipTestDocumentsResponse>(
      `/v1/relationships/${variables.relationshipId}/testing-flow/documents` + urlParams,
    );

    return {
      ...data,
      items: data.items.map((transaction) => ({
        ...transaction,
        internalType: determineInternalType(transaction),
      })),
    };
  },
});

type GetSenderProcessSpecificationResponse =
  portalSchema.paths['/v1/relationships/{relationship_id}/sender-process-specification']['get']['responses']['200']['content']['application/json'];
export const useSenderProcessSpecification = createQuery<
  GetSenderProcessSpecificationResponse['data'],
  { relationshipId: string | undefined; sourceMessageId: string | undefined },
  Error
>({
  primaryKey: `${RELATIONSHIPS_QUERY_KEY}/sender-process-specification`,
  queryFn: async ({ queryKey: [_, variables] }) => {
    const { data } = await fetchData<GetSenderProcessSpecificationResponse>(
      `/v1/relationships/${variables.relationshipId}/sender-process-specification?source_message_id=${variables.sourceMessageId}`,
    );
    return data.data;
  },
  enabled: (_, variables) => !!variables.relationshipId,
});

type GetReceiverProcessSpecificationResponse =
  portalSchema.paths['/v1/relationships/{relationship_id}/receiver-process-specification']['get']['responses']['200']['content']['application/json'];
export const useReceiverProcessSpecification = createQuery<
  GetReceiverProcessSpecificationResponse['data'],
  { relationshipId: string | undefined; sourceMessageId: string | undefined },
  Error
>({
  primaryKey: `${RELATIONSHIPS_QUERY_KEY}/receiver-process-specification`,
  queryFn: async ({ queryKey: [_, variables] }) => {
    const { data } = await fetchData<GetReceiverProcessSpecificationResponse>(
      `/v1/relationships/${variables.relationshipId}/receiver-process-specification?source_message_id=${variables.sourceMessageId}`,
    );
    return data.data;
  },
  enabled: (_, variables) => !!variables.relationshipId,
});

export type BulkRequestDocumentFromSenderRequest =
  portalSchema.paths['/v1/relationships/request-document']['post']['requestBody']['content']['application/json'];

type BulkRequestDocumentFromSenderResponse =
  portalSchema.paths['/v1/relationships/request-document']['post']['responses']['200']['content']['application/json'];

export const useBulkRequestDocumentFromSender = createMutation<
  BulkRequestDocumentFromSenderResponse,
  BulkRequestDocumentFromSenderRequest,
  Error
>({
  mutationFn: async (request) => {
    const { data } = await createData<BulkRequestDocumentFromSenderResponse>(
      `/v1/relationships/request-document`,
      request,
    );
    return data;
  },
});

export const useHasRelationshipForDocumentType = (documentType: DocumentType, state: RelationshipState) => {
  const { data } = useInfiniteRelationships({
    variables: {
      query: {
        filterValues: [
          { key: 'message_type', value: documentType },
          { key: 'state', value: state },
        ],
        perPage: '1',
      },
    },
  });

  const hasRelationship = useMemo(() => {
    return data?.pages?.some((page) => page.data.length > 0) ?? false;
  }, [data]);

  return hasRelationship;
};

type GetUploadDocumentUrlResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/file-upload-url']['post']['responses']['200']['content']['application/json'];

type GetUploadDocumentUrlRequest =
  portalSchema.paths['/v1/relationships/{relationshipId}/file-upload-url']['post']['requestBody']['content']['application/json'];

export const useGetRelationshipUploadDocumentUrl = createMutation<
  GetUploadDocumentUrlResponse['data'],
  { relationshipId: string; payload: GetUploadDocumentUrlRequest },
  Error
>({
  mutationFn: async ({ relationshipId, payload }) => {
    const { data } = await createData<GetUploadDocumentUrlResponse>(
      `/v1/relationships/${relationshipId}/file-upload-url`,
      payload,
    );
    return data?.data;
  },
});

export type GetRelationshipResponsibleUsersResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/responsible']['get']['responses']['200']['content']['application/json'];

export const useRelationshipResponsibleUsers = createQuery<
  GetRelationshipResponsibleUsersResponse['data'],
  { relationshipId: string },
  Error
>({
  primaryKey: `${RELATIONSHIPS_QUERY_KEY}/responsible`,
  queryFn: async ({ queryKey: [, variables] }) => {
    const { data } = await fetchData<GetRelationshipResponsibleUsersResponse>(
      `/v1/relationships/${variables.relationshipId}/responsible`,
    );
    return data?.data;
  },
});

export type UpdateRelationshipResponsibleUsersRequest =
  portalSchema.paths['/v1/relationships/{relationshipId}/responsible']['put']['requestBody']['content']['application/json'];

export type UpdateRelationshipResponsibleUsersResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/responsible']['put']['responses']['200']['content']['application/json'];

export const useUpdateRelationshipResponsibleUsers = createMutation<
  UpdateRelationshipResponsibleUsersResponse,
  { relationshipId: string; payload: UpdateRelationshipResponsibleUsersRequest },
  Error
>({
  mutationFn: async ({ relationshipId, payload }) => {
    const { data } = await updateData<UpdateRelationshipResponsibleUsersResponse>(
      `/v1/relationships/${relationshipId}/responsible`,
      payload,
    );
    return data;
  },
});
